import {
  ARENA_BATTLE_RECORD_SCHEMA_VERSION,
  seriesEndingOnSettledPnl,
  seriesThroughKnockout,
} from "./battle-record.dto";
import { Decimal } from "decimal.js";
import { z } from "zod";

// Each step reads only the fields it rewrites and passes the rest through; the caller validates the end result.
const V1Schema = z.looseObject({
  players: z.array(z.looseObject({ hubUserId: z.number(), finalPnlUsd: z.string() })),
  cuts: z.array(
    z.looseObject({
      skipped: z.boolean(),
      out: z.array(z.looseObject({ hubUserId: z.number(), pnlUsd: z.string().nullable() })),
    }),
  ),
});

const V2Schema = z.looseObject({
  players: z.array(
    z.looseObject({
      out: z.looseObject({ at: z.string() }).nullable(),
      series: z.looseObject({ startMs: z.number(), stepMs: z.number(), pnlCents: z.array(z.number()) }),
    }),
  ),
});

const toCents = (usd: string): string => {
  const cents = new Decimal(usd).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  return cents.isZero() ? "0" : cents.toFixed();
};

/** v2 dropped `prizeUsd` and gave a cut player the PnL frozen at the cut, as the standings show it. */
function v1ToV2(stored: unknown): unknown {
  const record = V1Schema.parse(stored);
  const cutPnl = new Map(
    record.cuts
      .filter((cut) => !cut.skipped)
      .flatMap((cut) => cut.out.flatMap(({ hubUserId, pnlUsd }) => (pnlUsd == null ? [] : [[hubUserId, pnlUsd]]))),
  );
  return {
    ...record,
    players: record.players.map(({ prizeUsd: _prizeUsd, ...player }) => {
      const pnlUsd = cutPnl.get(player.hubUserId);
      return pnlUsd == null ? player : { ...player, finalPnlUsd: toCents(pnlUsd) };
    }),
  };
}

/** v3 ends a knocked-out player's series at their knockout. */
function v2ToV3(stored: unknown): unknown {
  const record = V2Schema.parse(stored);
  return {
    ...record,
    players: record.players.map((player) =>
      player.out == null
        ? player
        : { ...player, series: seriesThroughKnockout(player.series, Date.parse(player.out.at)) },
    ),
  };
}

const V3Schema = z.looseObject({
  cuts: z.array(z.looseObject({ out: z.array(z.looseObject({ pnlUsd: z.string().nullable() })) })),
});

/** v4 states a stored trade's close fields non-null, which every stored trade already was, and cut figures in cents. */
function v3ToV4(stored: unknown): unknown {
  const record = V3Schema.parse(stored);
  return {
    ...record,
    cuts: record.cuts.map((cut) => ({
      ...cut,
      out: cut.out.map((out) => ({ ...out, pnlUsd: out.pnlUsd == null ? null : toCents(out.pnlUsd) })),
    })),
  };
}

const V4Schema = z.looseObject({
  players: z.array(
    z.looseObject({
      finalPnlUsd: z.string(),
      series: z.looseObject({ startMs: z.number(), stepMs: z.number(), pnlCents: z.array(z.number()) }),
    }),
  ),
});

/** v5 ends every series on the player's settled figure, which a cut player already stores as the cut's. */
function v4ToV5(stored: unknown): unknown {
  const record = V4Schema.parse(stored);
  return {
    ...record,
    players: record.players.map((player) => ({
      ...player,
      series: seriesEndingOnSettledPnl(player.series, player.finalPnlUsd),
    })),
  };
}

/** Keyed by the version a step upgrades from; every version below the current one has its step. */
export const ARENA_BATTLE_RECORD_UPGRADES: Readonly<Record<number, (stored: unknown) => unknown>> = {
  1: v1ToV2,
  2: v2ToV3,
  3: v3ToV4,
  4: v4ToV5,
};

/**
 * A stored payload of `fromVersion` as the current version would have stored it. Pure; throws on a payload a step
 * cannot read. The result still has to pass `ArenaBattleRecordSchema`.
 */
export function upgradeArenaBattleRecord(stored: unknown, fromVersion: number): unknown {
  let record = stored;
  for (let version = fromVersion; version < ARENA_BATTLE_RECORD_SCHEMA_VERSION; version++) {
    const step = ARENA_BATTLE_RECORD_UPGRADES[version];
    if (step == null) throw new Error(`No battle record upgrade from v${version}`);
    record = step(record);
  }
  return record;
}
